
/**
 * Track Service - Handles API calls to the tracks endpoint
 */

import api from "./api";

/**
 * Sync a track with the tracks API
 * @param {Object} trackData - Track data to sync
 * @returns {Promise<Object>} Sync result
 */
export const syncTrack = async (trackData) => {
    try {
        console.log('Track sync triggered for category 7');

        // Create FormData object for multipart/form-data request
        const formData = new FormData();

        // Add required post_id
        if (trackData.post_id) {
            formData.append('post_id', trackData.post_id);
        }

        // Add optional track fields
        if (trackData.title) formData.append('title', trackData.title);
        if (trackData.description) formData.append('description', trackData.description);
        if (trackData.artist) formData.append('artist', trackData.artist);
        if (trackData.genre) formData.append('genre', trackData.genre);
        if (trackData.album) formData.append('album', trackData.album);
        if (trackData.year) formData.append('year', trackData.year);
        if (trackData.track_number) formData.append('track_number', trackData.track_number);
        if (trackData.cover_image_url) formData.append('cover_image_url', trackData.cover_image_url);
        // Media information fields
        if (trackData.writer) formData.append('writer', trackData.writer);
        if (trackData.composer_name) formData.append('composer_name', trackData.composer_name);
        if (trackData.copyright) formData.append('copyright', trackData.copyright);
        if (trackData.links) formData.append('links', trackData.links);

        // Add audio file (required)
        if (trackData.audio_file && trackData.audio_file instanceof File) {
            formData.append('audio_file', trackData.audio_file);
        }

        // Use POST /tracks/sync to create or update track for existing post
        const response = await api.post('/tracks/sync', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        console.log('Track sync successful:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error syncing track:', error);
        // We don't throw the error here as per requirements - this should not block the main flow
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to sync track'
        };
    }
};

/**
 * Update track metadata (without uploading a new file)
 * @param {number} postId - Post ID
 * @param {Object} metadata - Metadata to update
 * @returns {Promise<Object>} Update result
 */
export const updateTrackMetadata = async (postId, metadata) => {
    try {
        console.log('Updating track metadata for post:', postId);

        const formData = new FormData();
        if (metadata.title) formData.append('title', metadata.title);
        if (metadata.description) formData.append('description', metadata.description);
        if (metadata.cover_image_url) formData.append('cover_image_url', metadata.cover_image_url);
        if (metadata.artist) formData.append('artist', metadata.artist);
        if (metadata.genre) formData.append('genre', metadata.genre);
        if (metadata.album) formData.append('album', metadata.album);
        // Media information fields
        if (metadata.writer) formData.append('writer', metadata.writer);
        if (metadata.composer_name) formData.append('composer_name', metadata.composer_name);
        if (metadata.copyright) formData.append('copyright', metadata.copyright);
        if (metadata.links) formData.append('links', metadata.links);

        const response = await api.put(`/tracks/${postId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        console.log('Track metadata update successful');
        return { success: true, data: response.data };
    } catch (error) {
        console.error('Error updating track metadata:', error);
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to update track metadata'
        };
    }
};

/**
 * Get streaming URL for a video
 * @param {number} videoId - Video ID
 * @returns {Promise<Object>} Stream data with URL and expiry
 */
export const getTrackStreamUrl = async (trackId) => {
    try {
        const response = await api.get(`/tracks/${trackId}/stream`);
        return response.data;
    } catch (error) {
        console.log('Error getting track stream URL:', error);
    }
};

/**
 * Get streaming URL for a video
 * @param {number} trackId - Video ID
 * @returns {Promise<Object>} Stream data with URL and expiry
 */
export const getTrackStreamUrlForPosts = async (trackId) => {
    try {
        const response = await api.get(`/tracks/post/${trackId}/stream`);
        return response.data;
    } catch (error) {
        console.log('Error getting video stream URL:', error);
        throw error;
    }
};

export const deleteTrack = async (trackId) => {
    try {
        await api.delete(`/tracks/${trackId}`);
        return true;
    } catch (error) {
        console.log('Failed to delete track');
    }
};

// Export all functions as default object for easier importing
const trackService = {
    syncTrack,
    updateTrackMetadata,
    getTrackStreamUrl,
    getTrackStreamUrlForPosts,
    deleteTrack,
};

export default trackService;
